// API service for Stripe-related operations
import axios from 'axios';

const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl && envUrl.trim() !== '') return envUrl;
  if (import.meta.env.PROD) return '';
  return 'http://localhost:5220';
};
const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================
// Deposits API
// ============================================

export const depositsAPI = {
  // Create deposit payment intent
  createDeposit: async (amountInDollars: number) => {
    const response = await api.post('/api/deposits/create', {
      amountInDollars,
    });
    return response.data;
  },

  // Check payment status
  checkStatus: async (paymentIntentId: string) => {
    const response = await api.get(`/api/deposits/status/${paymentIntentId}`);
    return response.data;
  },

  // Get deposit history
  getHistory: async () => {
    const response = await api.get('/api/deposits/history');
    return response.data;
  },
};

// ============================================
// Withdrawals API
// ============================================

export const withdrawalsAPI = {
  // Request withdrawal
  requestWithdrawal: async (amountInDollars: number, instantPayout: boolean = false) => {
    const response = await api.post('/api/withdrawals/request', {
      amountInDollars,
      instantPayout,
    });
    return response.data;
  },

  // Get withdrawal status
  getStatus: async (payoutId: string) => {
    const response = await api.get(`/api/withdrawals/status/${payoutId}`);
    return response.data;
  },

  // Get withdrawal history
  getHistory: async () => {
    const response = await api.get('/api/withdrawals/history');
    return response.data;
  },

  // Get available balance
  getAvailableBalance: async () => {
    const response = await api.get('/api/withdrawals/available-balance');
    return response.data;
  },
};

// ============================================
// Payment Methods API
// ============================================

export const paymentMethodsAPI = {
  // Setup card payment method
  setupCard: async () => {
    const response = await api.post('/api/payment-methods/setup/card');
    return response.data;
  },

  // Setup ACH bank account
  setupBankAccount: async () => {
    const response = await api.post('/api/payment-methods/setup/bank-account');
    return response.data;
  },

  // List saved payment methods
  list: async () => {
    const response = await api.get('/api/payment-methods/list');
    return response.data;
  },

  // Set default payment method
  setDefault: async (paymentMethodId: string) => {
    const response = await api.post('/api/payment-methods/set-default', {
      paymentMethodId,
    });
    return response.data;
  },

  // Remove payment method
  remove: async (paymentMethodId: string) => {
    const response = await api.delete(`/api/payment-methods/${paymentMethodId}`);
    return response.data;
  },
};

// ============================================
// Auto-Refill API
// ============================================

export const autoRefillAPI = {
  // Get auto-refill settings
  getSettings: async () => {
    const response = await api.get('/api/auto-refill/settings');
    return response.data;
  },

  // Enable auto-refill
  enable: async (
    thresholdAmount: number,
    refillAmount: number,
    paymentMethodId: string
  ) => {
    const response = await api.post('/api/auto-refill/enable', {
      thresholdAmount,
      refillAmount,
      paymentMethodId,
    });
    return response.data;
  },

  // Disable auto-refill
  disable: async () => {
    const response = await api.post('/api/auto-refill/disable');
    return response.data;
  },
};

export default api;
